import api from "./api";

/* ---------------------------------- MOCKS --------------------------------- */

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const MOCK_PARTY_UUID = "550e8400-e29b-41d4-a716-446655440000";

const emailSeed = [
  {
    party_uuid: MOCK_PARTY_UUID,
    sender: "advisor@dealer.com",
    subject: "Service appointment follow-up",
    opens: 3,
    clicks: 1,
    state: "Sent",
    thread: 42,
    created_at: "2025-11-01T10:00:00Z",
    last_update: "2025-11-01T10:05:30Z",
  },
  {
    party_uuid: MOCK_PARTY_UUID,
    sender: "support@dealer.com",
    subject: "Warranty notification",
    opens: 5,
    clicks: 0,
    state: "Opened",
    thread: 17,
    created_at: "2025-10-20T12:30:00Z",
    last_update: "2025-10-21T09:15:00Z",
  },
  {
    party_uuid: "b4f0f4dd-8ff8-4e57-b29d-1c0f4a7d4a00",
    sender: "sales@dealer.com",
    subject: "Exclusive offer for you",
    opens: 1,
    clicks: 0,
    state: "Clicked",
    thread: 8,
    created_at: "2025-09-10T08:45:00Z",
    last_update: "2025-09-10T09:00:00Z",
  },
];

const noteSeed = [
  {
    note_uuid: "11111111-1111-1111-1111-111111111111",
    party_uuid: MOCK_PARTY_UUID,
    noteTitle: "Service follow-up plan",
    description: "Customer agreed to bring the vehicle next Monday.",
    category: "Service",
    ownerFullName: "John Doe",
    createdAt: "2025-11-01T10:00:00Z",
    lastUpdate: "2025-11-01T10:05:30Z",
  },
  {
    note_uuid: "22222222-2222-2222-2222-222222222222",
    party_uuid: MOCK_PARTY_UUID,
    noteTitle: "Finance discussion recap",
    description: "Discussed financing options with the customer.",
    category: "Finance",
    ownerFullName: "Jane Smith",
    createdAt: "2025-10-15T14:10:00Z",
    lastUpdate: "2025-10-15T14:45:00Z",
  },
  {
    note_uuid: "33333333-3333-3333-3333-333333333333",
    party_uuid: "b4f0f4dd-8ff8-4e57-b29d-1c0f4a7d4a00",
    noteTitle: "Courtesy call summary",
    description: "Called to check on vehicle satisfaction.",
    category: "General",
    ownerFullName: "Chris Lee",
    createdAt: "2025-08-01T09:15:00Z",
    lastUpdate: "2025-08-01T09:30:00Z",
  },
];

const smsSeed = [
  {
    party_uuid: MOCK_PARTY_UUID,
    sms_name: "Promo reminder",
    from: "+12025550123",
    to: "+12025559876",
    replies: 1,
    status: "Delivered",
    body: "Your service is due next week.",
    created_at: "2025-11-01T10:00:00Z",
    last_update: "2025-11-01T10:05:30Z",
  },
  {
    party_uuid: MOCK_PARTY_UUID,
    sms_name: "Service confirmation",
    from: "+12025550123",
    to: "+12025559876",
    replies: 0,
    status: "Sent",
    body: "Your appointment is confirmed for tomorrow.",
    created_at: "2025-10-22T15:30:00Z",
    last_update: "2025-10-22T15:31:00Z",
  },
  {
    party_uuid: "b4f0f4dd-8ff8-4e57-b29d-1c0f4a7d4a00",
    sms_name: "Thank you message",
    from: "+12025559999",
    to: "+12025558888",
    replies: 0,
    status: "Sent",
    body: "Thank you for visiting our dealership.",
    created_at: "2025-07-04T11:00:00Z",
    last_update: "2025-07-04T11:02:00Z",
  },
];

/* --------------------------- Collection helpers ------------------------ */

const normalizeOrderArgs = (orderBy = [], orderDirection = []) => {
  const fields = Array.isArray(orderBy) && orderBy.length ? orderBy : ["created_at"];
  const directions =
    Array.isArray(orderDirection) && orderDirection.length === fields.length
      ? orderDirection
      : new Array(fields.length).fill("DESC");
  return { fields, directions };
};

const sortCollection = (collection, orderBy, orderDirection) => {
  const { fields, directions } = normalizeOrderArgs(orderBy, orderDirection);

  return [...collection].sort((a, b) => {
    for (let i = 0; i < fields.length; i += 1) {
      const field = fields[i];
      const direction = directions[i] === "ASC" ? 1 : -1;
      const aValue = a[field];
      const bValue = b[field];

      if (!aValue && !bValue) continue;
      if (!aValue) return 1 * direction;
      if (!bValue) return -1 * direction;

      const aDate = new Date(aValue).getTime();
      const bDate = new Date(bValue).getTime();

      if (Number.isNaN(aDate) || Number.isNaN(bDate)) {
        if (aValue > bValue) return 1 * direction;
        if (aValue < bValue) return -1 * direction;
        continue;
      }

      if (aDate > bDate) return 1 * direction;
      if (aDate < bDate) return -1 * direction;
    }
    return 0;
  });
};

const paginateCollection = (collection, page = 1, pageSize = 20) => {
  const safePage = Number.isInteger(page) && page > 0 ? page : 1;
  const safePageSize =
    Number.isInteger(pageSize) && pageSize >= 1 && pageSize <= 100 ? pageSize : 20;

  const startIndex = (safePage - 1) * safePageSize;
  const endIndex = startIndex + safePageSize;
  const sliced = collection.slice(startIndex, endIndex);

  const totalItems = collection.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));

  return {
    items: sliced,
    meta: {
      total_items: totalItems,
      total_pages: totalPages,
      current_page: safePage,
      per_page: safePageSize,
      has_next: safePage < totalPages,
      has_previous: safePage > 1,
      next_cursor: null,
    },
  };
};

const filterByDateRange = (collection, field, range = {}) => {
  if (!range || (!range.from && !range.to)) return collection;

  const fromTime = range.from ? new Date(range.from).getTime() : null;
  const toTime = range.to ? new Date(range.to).getTime() : null;

  return collection.filter((item) => {
    const valueTime = new Date(item[field]).getTime();
    if (Number.isNaN(valueTime)) return false;
    if (fromTime && valueTime < fromTime) return false;
    if (toTime && valueTime > toTime) return false;
    return true;
  });
};

const buildMockResponse = (items, meta, msgCode) => ({
  status_code: 200,
  msg_code: [
    {
      code: msgCode,
      description: {
        ACT_EMAIL_LIST_OK: "Email activities listed successfully.",
        ACT_NOTE_LIST_OK: "Notes listed successfully.",
        ACT_SMS_LIST_OK: "SMS activities listed successfully.",
      }[msgCode],
    },
  ],
  request_id: `mock-${msgCode}-${Date.now()}`,
  body: items,
  meta,
});

/* ------------------------------ Mock services ----------------------------- */

const getMockEmailActivities = async (params = {}) => {
  await delay(400);
  const {
    party_uuid,
    filters = {},
    search = {},
    order_by,
    order_direction,
    page,
    page_size,
  } = params;

  const keyword = (search.keyword || "").trim().toLowerCase();
  const stateFilter = filters.state ? String(filters.state).toUpperCase() : null;
  const lastUpdateRange = filters.last_update;

  let collection = emailSeed.filter(
    (item) => !party_uuid || item.party_uuid === party_uuid
  );

  if (collection.length === 0 && party_uuid) {
    collection = [...emailSeed];
  }

  if (stateFilter) {
    collection = collection.filter((item) => item.state === stateFilter);
  }

  collection = filterByDateRange(collection, "last_update", lastUpdateRange);

  if (keyword) {
    collection = collection.filter(
      (item) =>
        item.subject.toLowerCase().includes(keyword) ||
        item.sender.toLowerCase().includes(keyword)
    );
  }

  const sorted = sortCollection(collection, order_by, order_direction);
  const { items, meta } = paginateCollection(sorted, page, page_size);

  return buildMockResponse(items, meta, "ACT_EMAIL_LIST_OK");
};

const getMockNoteActivities = async (params = {}) => {
  await delay(400);
  const {
    party_uuid,
    filters = {},
    search = {},
    order_by,
    order_direction,
    page,
    page_size,
  } = params;

  const keyword = (search.keyword || "").trim().toLowerCase();
  const categoryFilter = filters.category ? String(filters.category).toUpperCase() : null;
  const lastUpdateRange = filters.last_update;

  let collection = noteSeed.filter(
    (item) => 
      !item.marked_deleted && // Filter out deleted notes
      (!party_uuid || item.party_uuid === party_uuid)
  );

  if (collection.length === 0 && party_uuid) {
    collection = noteSeed.filter((item) => !item.marked_deleted);
  }

  if (categoryFilter) {
    collection = collection.filter((item) => item.category === categoryFilter);
  }

  collection = filterByDateRange(collection, "last_update", lastUpdateRange);

  if (keyword) {
    collection = collection.filter((item) =>
      item.note_title?.toLowerCase().includes(keyword) ||
      item.noteTitle?.toLowerCase().includes(keyword)
    );
  }

  const sorted = sortCollection(collection, order_by, order_direction);
  const { items, meta } = paginateCollection(sorted, page, page_size);

  return buildMockResponse(items, meta, "ACT_NOTE_LIST_OK");
};

const getMockSmsActivities = async (params = {}) => {
  await delay(400);
  const {
    party_uuid,
    filters = {},
    search = {},
    order_by,
    order_direction,
    page,
    page_size,
  } = params;

  const keyword = (search.keyword || "").trim().toLowerCase();
  const statusFilter = filters.status ? String(filters.status).toUpperCase() : null;
  const lastUpdateRange = filters.last_update;

  let collection = smsSeed.filter(
    (item) => !party_uuid || item.party_uuid === party_uuid
  );

  if (collection.length === 0 && party_uuid) {
    collection = [...smsSeed];
  }

  if (statusFilter) {
    collection = collection.filter((item) => item.status === statusFilter);
  }

  collection = filterByDateRange(collection, "last_update", lastUpdateRange);

  if (keyword) {
    collection = collection.filter((item) =>
      item.sms_name.toLowerCase().includes(keyword)
    );
  }

  const sorted = sortCollection(collection, order_by, order_direction);
  const { items, meta } = paginateCollection(sorted, page, page_size);

  return buildMockResponse(items, meta, "ACT_SMS_LIST_OK");
};

/* ------------------------- Payload & Normalizers ------------------------ */

/**
 * Build request payload for activity endpoints (snake_case)
 */
const buildRequestPayload = (params = {}, options = {}) => {
  const { stripPaging = false } = options;

  const {
    party_uuid,
    page = 1,
    page_size = 20,
    order_by = "created_at",
    order_direction = "DESC",
    search,
    filters,
    activity_type,
  } = params;

  const clean = (obj) =>
    Object.fromEntries(
      Object.entries(obj || {}).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );

  // Handle order_by - convert array to string if needed
  const orderByValue = Array.isArray(order_by) ? order_by[0] : order_by;
  const orderDirectionValue = Array.isArray(order_direction) ? order_direction[0] : order_direction;

  const base = {
    party_uuid,
    ...(Object.keys(clean(search || {})).length ? { search: clean(search) } : {}),
    ...(Object.keys(clean(filters || {})).length ? { filters: clean(filters) } : {}),
    ...(activity_type !== undefined ? { activity_type } : {}),
  };

  if (stripPaging) return base;

  return {
    ...base,
    page,
    page_size,
    order_by: orderByValue,
    order_direction: orderDirectionValue,
  };
};

/**
 * Build request payload for communication endpoint (camelCase)
 */
const buildCommunicationPayload = (params = {}) => {
  const {
    party_uuid,
    page = 1,
    page_size = 20,
    order_by = "started_at",
    order_direction = "DESC",
    search,
    originChannel,
  } = params;

  // Handle order_by - convert array to string if needed
  const orderByValue = Array.isArray(order_by) ? order_by[0] : order_by;
  const orderDirectionValue = Array.isArray(order_direction) ? order_direction[0] : order_direction;

  const payload = {
    party_uuid,
    page,
    page_size,
    order_by: orderByValue,
    order_direction: orderDirectionValue,
  };

  // Add originChannel filter if specified
  if (originChannel) {
    payload.originChannel = originChannel;
  }

  // Add search keyword
  if (search?.keyword?.trim()) {
    payload.searchKeyword = search.keyword.trim();
  }

  return payload;
};

/**
 * Normalize meta fields from camelCase (API) to snake_case (internal)
 */
const normalizeMeta = (meta) => {
  if (!meta) {
    return {
      total_items: 0,
      total_pages: 1,
      current_page: 1,
      per_page: 20,
      has_next: false,
      has_previous: false,
      next_cursor: null,
    };
  }

  return {
    total_items: meta.totalItems ?? meta.total_items ?? 0,
    total_pages: meta.totalPages ?? meta.total_pages ?? 1,
    current_page: meta.currentPage ?? meta.current_page ?? 1,
    per_page: meta.perPage ?? meta.per_page ?? 20,
    has_next: meta.hasNext ?? meta.has_next ?? false,
    has_previous: meta.hasPrevious ?? meta.has_previous ?? false,
    next_cursor: meta.nextCursor ?? meta.next_cursor ?? null,
  };
};

/**
 * Normalize /v1/workspace/communication/list flat array to { email, sms, note }
 * for ActivityContext. API items have title, originChannel, startedAt, lastUpdate, metadata.
 */
const normalizeCommunicationListToLegacy = (arr) => {
  const email = [];
  const sms = [];
  const note = [];
  (arr || []).forEach((item) => {
    const channel = (item.originChannel || item.origin_channel || "").toUpperCase();
    const title = item.title || "";
    const startedAt = item.startedAt || item.started_at;
    const lastUpdate = item.lastUpdate || item.last_update;
    const conversationUuid = item.conversationUuid || item.conversation_uuid;
    const status = item.status || "ACTIVE";
    const metadata = item.metadata || item.meta || {};
    const bound = metadata.bound || {};

    if (channel === "EMAIL") {
      email.push({ subject: title, sender: "Email", created_at: startedAt, last_update: lastUpdate, thread: conversationUuid, state: status, opens: 0, clicks: 0 });
    } else if (channel === "SMS") {
      const threadId = metadata.thread_id || metadata.threadId || "";
      const topic = metadata.topic || "";
      sms.push({
        sms_name: title, body: title, created_at: startedAt, last_update: lastUpdate, status,
        from: item.from ?? metadata.from ?? bound.phone ?? "SMS",
        to: item.to ?? metadata.to ?? bound.customer_phone ?? (threadId || topic || "Customer"),
        replies: item.replies ?? metadata.replies ?? 0,
      });
    } else {
      note.push({ note_title: title, noteTitle: title, description: title, category: channel || "GENERAL", owner_full_name: "", ownerFullName: "", last_update: lastUpdate, lastUpdate: lastUpdate, created_at: startedAt, createdAt: startedAt });
    }
  });
  return { email, sms, note };
};

const normalizeAllBody = (data) => {
  if (Array.isArray(data?.data)) return normalizeCommunicationListToLegacy(data.data);
  const body = data?.body ?? data ?? {};
  if (Array.isArray(body)) return normalizeCommunicationListToLegacy(body);
  return {
    email: Array.isArray(body.email) ? body.email : [],
    phone: Array.isArray(body.phone) ? body.phone : [],
    sms: Array.isArray(body.sms) ? body.sms : [],
    note: Array.isArray(body.note) ? body.note : [],
    repair_order: Array.isArray(body.repair_order) ? body.repair_order : [],
  };
};

const normalizeAllResponse = (resp) => {
  const data = resp?.data ?? resp;
  // Handle meta from either data.meta or top-level meta
  const meta = data?.meta ?? resp?.meta;
  return { body: normalizeAllBody(data), meta: normalizeMeta(meta) };
};

const normalizeSingle = (resp) => {
  const data = resp?.data ?? resp;
  const arr = Array.isArray(data?.body) ? data.body : [];
  return {
    body: arr,
    meta: normalizeMeta(data?.meta),
  };
};

/* -------------------------- Unified Endpoint (ALL) ----------------------- */

export const listActivitiesAll = async (params = {}) => {
  try {
    const body = buildRequestPayload(params);
    const response = await api.post("/v1/workspace/communication/list", body);
    return normalizeAllResponse(response);
  } catch (error) {
    console.error("listActivitiesAll error:", error?.response?.data || error?.message);
    return normalizeAllResponse({
      body: { email: [], phone: [], sms: [], note: [], repair_order: [] },
      meta: null,
    });
  }
};

/* ---------------------------- Individual endpoints ----------------------- */

export const listEmailActivities = async (params = {}) => {
  try {
    const body = buildRequestPayload(params);
    const response = await api.post("/v1/workspace/activity/email/list", body);
    return normalizeSingle(response);
  } catch (error) {
    console.warn("listEmailActivities failed", error?.response?.data || error?.message);
    return normalizeSingle({ body: [], meta: null });
  }
};

export const listNoteActivities = async (params = {}) => {
  try {
    const body = buildRequestPayload(params);
    const response = await api.post("/v1/workspace/activity/note/list", body);
    return normalizeSingle(response);
  } catch (error) {
    console.warn("listNoteActivities failed", error?.response?.data || error?.message);
    return normalizeSingle({ body: [], meta: null });
  }
};

export const listSmsActivities = async (params = {}) => {
  try {
    const body = buildRequestPayload(params);
    const response = await api.post("/v1/workspace/activity/sms/list", body);
    return normalizeSingle(response);
  } catch (error) {
    console.warn("listSmsActivities failed", error?.response?.data || error?.message);
    return normalizeSingle({ body: [], meta: null });
  }
};

/* ---------------------------- Create Note ----------------------- */

export const createNote = async (noteData) => {
  if (!noteData.entity_type) {
    throw new Error("entity_type is required");
  }
  if (!noteData.entity_uuid) {
    throw new Error("entity_uuid is required");
  }
  if (!noteData.note_title?.trim()) {
    throw new Error("note_title is required and cannot be empty");
  }
  if (!noteData.note_content?.trim()) {
    throw new Error("note_content is required and cannot be empty");
  }

  const payload = {
    entity_type: noteData.entity_type,
    entity_uuid: noteData.entity_uuid,
    note_title: noteData.note_title.trim(),
    note_content: noteData.note_content.trim(),
    categories: Array.isArray(noteData.categories) ? noteData.categories : [],
    tags: Array.isArray(noteData.tags) ? noteData.tags : [],
  };

  try {
    const response = await api.post("/v1/note/create", payload);

    return {
      status_code: response.data?.status_code || response.status,
      msg_code: response.data?.msg_code || [],
      request_id: response.data?.request_id,
      body: response.data?.body || {},
      meta: response.data?.meta || null,
    };
  } catch (error) {
    console.error("Error creating note:", error?.response?.data || error?.message);

    if (error?.response?.data) {
      const apiError = new Error(
        error.response.data.msg_code?.[0]?.description || error.message
      );
      apiError.status_code = error.response.data.status_code || error.response.status;
      apiError.msg_code = error.response.data.msg_code || [];
      throw apiError;
    }
    throw error;
  }
};

/* ---------------------------- Get Note Detail ----------------------- */

// Helper to strip HTML tags from text
const stripHtmlTagsForDetail = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

export const getNoteDetail = async (noteUuid) => {
  if (!noteUuid) {
    throw new Error("note_uuid is required");
  }

  try {
    // Use correct endpoint: /v1/notes/{id} (plural)
    const response = await api.get(`/v1/notes/${noteUuid}`);
    
    // Transform response to match expected format by NotesSlideOut component
    // Check if response.data has a nested body or data property
    const rawBody = response.data?.body || response.data?.data || response.data || {};
    const transformedBody = {
      note_uuid: rawBody.noteUuid || noteUuid,
      note_id: rawBody.noteId,
      // Use noteTitle (API returns this) or title as fallback
      note_title: stripHtmlTagsForDetail(rawBody.noteTitle || rawBody.title || ''),
      // Use description (API returns this) or content as fallback
      note_content: rawBody.description || rawBody.content || '',
      category: rawBody.category || rawBody.categories?.[0] || '',
      categories: rawBody.categories || [],
      tags: rawBody.tags || [],
      memos: rawBody.memos || [],
      entity_type: rawBody.entityType,
      entity_uuid: rawBody.entityUuid,
      // Owner info - use ownerFullName (API returns this)
      ownerFullName: rawBody.ownerFullName,
      owner_full_name: rawBody.ownerFullName || rawBody.owner_full_name || rawBody.createdByName || rawBody.created_by_name || rawBody.createdBy?.name,
      owner_avatar: rawBody.ownerAvatar || rawBody.owner_avatar || rawBody.createdBy?.avatar,
      // Dates - use createdAt and lastUpdate (API returns these)
      createdAt: rawBody.createdAt,
      created_at: rawBody.createdAt || rawBody.created_at || rawBody.createDate,
      lastUpdate: rawBody.lastUpdate,
      last_update: rawBody.lastUpdate || rawBody.updatedAt || rawBody.updated_at || rawBody.last_update || rawBody.updateDate,
      // Visibility
      visibility: rawBody.visibility || 'PRIVATE',
      is_team_note: rawBody.isTeamNote || rawBody.is_team_note,
      // Keep raw data for reference
      raw: rawBody,
    };

    return {
      status_code: response.status,
      msg_code: [],
      request_id: null,
      body: transformedBody,
      meta: null,
    };
  } catch (error) {
    console.error("Error getting note detail:", error?.response?.data || error?.message);

    if (error?.response?.data) {
      const apiError = new Error(
        error.response.data.msg_code?.[0]?.description || error.message
      );
      apiError.status_code = error.response.data.status_code || error.response.status;
      apiError.msg_code = error.response.data.msg_code || [];
      throw apiError;
    }

    throw error;
  }
};

/* ---------------------------- Update Note ----------------------- */

export const updateNote = async (noteData) => {
  if (!noteData.note_uuid) {
    throw new Error("note_uuid is required");
  }

  const payload = {
    note_uuid: noteData.note_uuid,
    note_title: noteData.note_title?.trim(),
    note_content: noteData.note_content?.trim(),
    categories: Array.isArray(noteData.categories) ? noteData.categories : [],
    tags: Array.isArray(noteData.tags) ? noteData.tags : [],
  };

  try {
    const response = await api.post("/v1/note/update", payload);

    return {
      status_code: response.data?.status_code || response.status,
      msg_code: response.data?.msg_code || [],
      request_id: response.data?.request_id,
      body: response.data?.body || {},
      meta: response.data?.meta || null,
    };
  } catch (error) {
    console.error("Error updating note:", error?.response?.data || error?.message);

    if (error?.response?.data) {
      const apiError = new Error(
        error.response.data.msg_code?.[0]?.description || error.message
      );
      apiError.status_code = error.response.data.status_code || error.response.status;
      apiError.msg_code = error.response.data.msg_code || [];
      throw apiError;
    }

    throw error;
  }
};

/* ---------------------------- Delete Note ----------------------- */

const getMockDeleteNote = async (noteUuid) => {
  await delay(300);

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(noteUuid)) {
    return {
      status_code: 400,
      msg_code: [
        {
          code: "INVALID_NOTE_UUID",
          description: "Provided note_uuid is not a valid UUID.",
        },
      ],
      request_id: `mock-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  const noteIndex = noteSeed.findIndex(
    (note) => note.note_uuid === noteUuid
  );

  if (noteIndex === -1) {
    return {
      status_code: 404,
      msg_code: [
        {
          code: "NOTE_NOT_FOUND",
          description: "Note not found or already deleted.",
        },
      ],
      request_id: `mock-${Date.now()}`,
      body: null,
      meta: null,
    };
  }

  if (!noteSeed[noteIndex].marked_deleted) {
    noteSeed[noteIndex].marked_deleted = true;
  }

  return {
    status_code: 200,
    msg_code: [
      {
        code: "NOTE_DELETED",
        description: "Note deleted successfully.",
      },
    ],
    request_id: `mock-${Date.now()}`,
    body: {
      note_uuid: noteUuid,
    },
    meta: null,
  };
};

export const deleteNote = async (noteUuid) => {
  if (!noteUuid) {
    throw new Error("note_uuid is required");
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(noteUuid)) {
    throw new Error("note_uuid must be a valid UUID");
  }

  const payload = {
    note_uuid: noteUuid,
  };

  try {
    const response = await api.post("/v1/note/delete", payload);

    return {
      status_code: response.data?.status_code || response.status,
      msg_code: response.data?.msg_code || [],
      request_id: response.data?.request_id,
      body: response.data?.body || {},
      meta: response.data?.meta || null,
    };
  } catch (error) {
    console.error("Error deleting note:", error?.response?.data || error?.message);

    if (error?.response?.status === 404 || error?.response?.status === 500) {
      return await getMockDeleteNote(noteUuid);
    }

    if (error?.response?.data) {
      const apiError = new Error(
        error.response.data.msg_code?.[0]?.description || error.message
      );
      apiError.status_code = error.response.data.status_code || error.response.status;
      apiError.msg_code = error.response.data.msg_code || [];
      throw apiError;
    }

    throw error;
  }
};

/**
 * Mark communications as read
 * @param {string[]} communicationIds - Array of communication IDs
 * @returns {Promise<Object>} API response
 */
export const markCommunicationsAsRead = async (communicationIds) => {
  if (!communicationIds || communicationIds.length === 0) {
    throw new Error('Communication IDs are required');
  }

  try {
    const response = await api.post('/v1/activity/communications/mark-read', {
      communication_uuids: communicationIds,
    });

    return {
      success: true,
      body: response.data?.body || response.data,
    };
  } catch (error) {
    console.error('Error marking communications as read:', error);
    throw error;
  }
};

/**
 * Assign communications to an agent/user
 * @param {string[]} communicationIds - Array of communication IDs
 * @param {string} assigneeId - UUID of the user to assign to (optional for now)
 * @returns {Promise<Object>} API response
 */
export const assignCommunications = async (communicationIds, assigneeId = null) => {
  if (!communicationIds || communicationIds.length === 0) {
    throw new Error('Communication IDs are required');
  }

  try {
    const response = await api.post('/v1/activity/communications/assign', {
      communication_uuids: communicationIds,
      assignee_uuid: assigneeId,
    });

    return {
      success: true,
      body: response.data?.body || response.data,
    };
  } catch (error) {
    console.error('Error assigning communications:', error);
    throw error;
  }
};

/**
 * Close communications
 * @param {string[]} communicationIds - Array of communication IDs
 * @returns {Promise<Object>} API response
 */
export const closeCommunications = async (communicationIds) => {
  if (!communicationIds || communicationIds.length === 0) {
    throw new Error('Communication IDs are required');
  }

  try {
    const response = await api.post('/v1/activity/communications/close', {
      communication_uuids: communicationIds,
    });

    return {
      success: true,
      body: response.data?.body || response.data,
    };
  } catch (error) {
    console.error('Error closing communications:', error);
    throw error;
  }
};
